import { AsyncPipe, NgClass } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { BehaviorSubject, Subscription, finalize } from 'rxjs';
import { NotificationService } from '../../services/notification.service';
import { UserInfoService } from '../../services/user-info.service';
import { AppConstant } from '../../utilities/app.constant';
import { CustomizableButtonComponent } from '../customizable-button/customizable-button.component';
import { SingleSwitchComponent } from '../form-control/single-switch/single-switch.component';
import { LoadingComponent } from '../loading/nonPercentageLoading/loading/loading.component';
import { KeyValueObject } from './notification-setting.interface';

@Component({
  selector: 'app-notification--setting',
  templateUrl: './notification-setting.component.html',
  styleUrls: ['./notification-setting.component.scss'],
  imports: [
    SingleSwitchComponent,
    CustomizableButtonComponent,
    ButtonModule,
    NgClass,
    LoadingComponent,
    AsyncPipe,
  ],
})
export class NotificationSettingComponent implements OnInit, OnDestroy {
  constructor(
    private userInfoService: UserInfoService,
    private notificationService: NotificationService,
    private dialogRef: DynamicDialogRef,
    public dialogConfig: DynamicDialogConfig
  ) {}

  private notiSettingSubscription: Subscription = new Subscription();
  public isLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  );

  settings: KeyValueObject[] = [
    {
      key: 'notiTeamsChat',
      name: 'Mentions you in a team chat',
      value: true,
    },
    {
      key: 'notiVideo',
      name: 'Mentions you in a video',
      value: true,
    },
    {
      key: 'notiAlert',
      name: 'Sends an alert',
      value: true,
    },
  ];

  settingsInit: KeyValueObject[] = [
    {
      key: 'notiTeamsChat',
      name: 'Mentions you in a team chat',
      value: false,
    },
    {
      key: 'notiVideo',
      name: 'Mentions you in a video',
      value: false,
    },
    {
      key: 'notiAlert',
      name: 'Sends an alert',
      value: false,
    },
  ];

  settingsDefault: KeyValueObject[] = [
    {
      key: 'notiTeamsChat',
      name: 'Mentions you in a team chat',
      value: false,
    },
    {
      key: 'notiVideo',
      name: 'Mentions you in a video',
      value: false,
    },
    {
      key: 'notiAlert',
      name: 'Sends an alert',
      value: false,
    },
  ];

  isViewer: boolean = false;
  isChange: boolean = false;

  // Chatbot sound notification settings
  ngOnInit(): void {
    this.isLoading$.next(true);
    if (this.dialogConfig.data.userId) {
      this.loadInitiaData(this.dialogConfig.data.userId);
    } else {
      this.notificationService.setMessage({
        type: AppConstant.MESSAGE_TYPE.WARNING,
        header: 'Information',
        content: 'Can not find user id',
      });
    }
  }

  ngOnDestroy(): void {
    this.isLoading$.next(false);
    this.notiSettingSubscription.unsubscribe();
  }

  loadInitiaData(loginID: any): void {
    this.isLoading$.next(true);
    this.notiSettingSubscription = this.userInfoService
      .getUserInfoInitiate(loginID)
      .pipe(
        finalize(() => {
          this.settingsInit = JSON.parse(JSON.stringify(this.settings));
        })
      )
      .subscribe({
        next: (userInfo: any) => {
          // Update Role Variable
          this.isViewer = userInfo.role === AppConstant.ROLES.VIEWER.label;

          if (!userInfo.userSetting) {
            this.settings = JSON.parse(JSON.stringify(this.settingsInit));
            this.isLoading$.next(false);
            return;
          }

          this.buildTemplate(userInfo.userSetting);
        },
        error: (err: any) => {
          this.notificationService.setMessage({
            type: AppConstant.MESSAGE_TYPE.WARNING,
            header: 'Information',
            content: err?.error,
          });
          this.isLoading$.next(false);
        },
      });
  }

  buildTemplate(dataSetting: any): void {
    this.settings.forEach(setting => {
      if (setting.key in dataSetting) {
        setting.value = dataSetting[setting.key];
      }
    });
    // Stable UI then Turn Off Loading
    this.isLoading$.next(false);
  }

  onToggle(value: boolean, index: number): void {
    this.settings[index].value = value;

    this.isChange = this.checkChange(this.settings, this.settingsInit);
  }

  checkChange(
    settings: KeyValueObject[],
    settingsInit: KeyValueObject[]
  ): boolean {
    const result = this.compareSetting(settings, settingsInit);
    return result;
  }

  compareSetting(arrA: KeyValueObject[], arrB: KeyValueObject[]): boolean {
    const mapB = new Map(arrB.map(item => [item.key, item.value]));
    for (const objA of arrA) {
      if (mapB.get(objA.key) !== objA.value) {
        return true;
      }
    }
    return false;
  }

  onReset(): void {
    this.settings = JSON.parse(JSON.stringify(this.settingsDefault));
    this.isChange = this.checkChange(this.settings, this.settingsInit);
  }

  onSave(): void {
    let payload: any = this.buildPayLoad();

    this.isLoading$.next(true);
    this.userInfoService
      .updateSetting(payload)
      .pipe(finalize(() => this.isLoading$.next(false)))
      .subscribe({
        next: (response: any) => {
          const { data, message } = response;
          this.onCancel();

          this.notificationService.setMessage({
            type: AppConstant.MESSAGE_TYPE.SUCCESS,
            header: 'User Preferences',
            content: `Manage Notifications was updated successfully.`,
          });
        },
        error: (err: any) => {
          this.notificationService.setMessage({
            type: AppConstant.MESSAGE_TYPE.WARNING,
            header: 'Information',
            content: err?.error,
          });
        },
      });
  }

  buildPayLoad(): any {
    return this.settings.reduce((acc: any, curr: any) => {
      const newKey = `is${curr.key.charAt(0).toUpperCase()}${curr.key.slice(1)}`;
      acc[newKey] = curr.value;
      return acc;
    }, {});
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
